import classnames from 'classnames';
import type React from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { useStore } from 'src/lib/player/store';
import { onlyMouseEvents } from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { SelectionControls } from './SelectionControls';

type Props = {
  id: string;
  kind: 'poi' | 'track';
  label: string;
  icon: React.ReactNode;
  // Shown after the event names, if any.
  info?: React.ReactNode;
  onClick: (isSelected: boolean) => void;
};

const EVENT_FEATURES = { poi: 'pois', track: 'tracks' } as const;

// A selectable, hideable sidebar row for a POI or a track.
export function MapFeatureListItem({ id, kind, label, icon, info, onClick }: Props): JSX.Element {
  const toggleHidden = useStore((state) => state.actions.toggleHidden);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);

  const isSelected = useStore((state) => state.timer.selected.includes(id));
  const isSelectedOrHighlighted = useStore(
    (state) => state.timer.highlighted === id || state.timer.selected.includes(id),
  );
  const isVisible = useStore((state) => !state.timer.hidden.has(id));
  const eventNames = useStore((state) =>
    state.currentEventIndices
      .filter((i) => state.events[i][EVENT_FEATURES[kind]].some((f) => f.id === id))
      .map((i) => state.events[i].shortName)
      .join(', '),
  );
  const eventCount = useStore((state) => state.currentEventIndices.length);

  function handleClick() {
    trackEvent('select_content', { content_type: kind, selected: !isSelected });
    toggleSelectedStarter(id);
    onClick(!isSelected);
  }

  function handleHideClick(
    ev:
      | React.MouseEvent<SVGSVGElement>
      | React.KeyboardEvent<SVGSVGElement>
      | React.TouchEvent<SVGSVGElement>,
  ) {
    ev.stopPropagation();
    toggleHidden(id);
  }

  return (
    <div
      className={classnames('starter-list-item', {
        selected: isSelectedOrHighlighted,
      })}
      {...onClickGenerator(handleClick)}
      onPointerEnter={onlyMouseEvents(() => {
        setHighlighted(id);
      })}
      onPointerLeave={onlyMouseEvents(() => {
        setHighlighted(null);
      })}
    >
      <div>
        {icon}
        <div className="starter-text" style={{ marginLeft: 5 }}>
          <div className="starter-name" title={label}>
            <div className="starter-name-content">{label}</div>
          </div>
          {(eventCount > 1 || info != null) && (
            <div className="starter-info">
              {eventCount > 1 && <span title={eventNames}>{eventNames}</span>}
              {info}
            </div>
          )}
        </div>
        <SelectionControls
          isSelected={isSelected}
          isVisible={isVisible}
          onHideClick={handleHideClick}
        />
      </div>
    </div>
  );
}
